import { Check, Clock3, Copy, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "mel_emergency_announcement_v1_dismissed";
const CODES = ["ILAC100", "ECTO100"];

const wasDismissed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const copyCode = async (code) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(code);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = code;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("clipboard");
};

const EmergencyAnnouncementBar = () => {
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [closing, setClosing] = useState(false);
  const [copiedCode, setCopiedCode] = useState("");
  const copiedTimer = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => () => {
    window.clearTimeout(copiedTimer.current);
    window.clearTimeout(closeTimer.current);
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "true"); } catch {}
    setClosing(true);
    closeTimer.current = window.setTimeout(() => setDismissed(true), 220);
  };

  const handleCopy = async (code) => {
    try {
      await copyCode(code);
      setCopiedCode(code);
      toast.success("Code Copied!", { description: `Use ${code} at checkout for 5 Free Group Classes.` });
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedCode(""), 2500);
    } catch {
      toast.error("Copy failed", { description: "Please select the code and copy it manually." });
    }
  };

  if (dismissed) return null;

  return (
    <aside className={`emergency-announcement-bar ${closing ? "is-closing" : ""}`} role="region" aria-label="Emergency student offer" data-testid="emergency-announcement-bar">
      <div className="emergency-announcement-inner">
        <p className="emergency-announcement-copy" data-testid="emergency-announcement-copy">📢 Displaced by ILAC or EC Toronto closures? Get 5 FREE Group Classes!</p>
        <div className="emergency-coupon-badges" aria-label="Emergency offer coupon codes">
          {CODES.map((code) => {
            const isCopied = copiedCode === code;
            return <button className={`emergency-coupon-button ${isCopied ? "is-copied" : ""}`} type="button" key={code} onClick={() => handleCopy(code)} aria-label={`Copy coupon code ${code}`} data-testid={`emergency-coupon-${code.toLowerCase()}`}><span className="emergency-coupon-code"><Copy size={12} aria-hidden="true" />{code}</span><span className="emergency-coupon-status" aria-live="polite" data-testid={isCopied ? "emergency-coupon-copied-indicator" : undefined}>{isCopied ? <><Check size={12} aria-hidden="true" />Code Copied!</> : "Copy"}</span></button>;
          })}
        </div>
        <span className="emergency-expiry-badge" data-testid="emergency-expiry-badge"><Clock3 size={13} aria-hidden="true" />Valid until Dec 31, 2026</span>
        <button className="emergency-dismiss-button" type="button" onClick={dismiss} aria-label="Dismiss emergency announcement" data-testid="emergency-dismiss-button"><X size={17} strokeWidth={2.2} aria-hidden="true" /></button>
      </div>
    </aside>
  );
};

export default EmergencyAnnouncementBar;
